import {captureException} from '@sentry/browser';
import {Button, DataStatus} from 'platform/components';
import {Center, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {usePostOAuthTokenMutation} from '@dms/api/base';
import {useLazyGetDocument360RedirectUrlQuery} from '@dms/api/security';
import {browserStorageKey, OAUTH_CLIENT_ID} from '@dms/config';
import i18n from '@dms/i18n';
import {
  clearOAuthFlowCookie,
  iamQueryParams,
  readOAuthFlowCookie,
  selectOAuthFlow,
  redirectToLogin,
} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {useOnMount} from 'shared/web';

import {getRedirectSearchParams} from './utils/getRedirectSearchParams';

export const Doc360Redirect = () => {
  const [postOAuthToken, {isError}] = usePostOAuthTokenMutation();
  const [getDocument360RedirectURl] = useLazyGetDocument360RedirectUrlQuery();

  const [areSearchParamsValid, params] = getRedirectSearchParams();

  const [candidates] = useState(readOAuthFlowCookie);
  const state = new URLSearchParams(window.location.search).get(iamQueryParams.STATE);
  const flow = selectOAuthFlow(candidates, state);
  const isStateValid = isNil(state) || !isNil(flow);
  const isRedirectValid = areSearchParamsValid && isStateValid;

  useOnMount(() => {
    clearOAuthFlowCookie();
    if (!areSearchParamsValid || !isStateValid) {
      captureException(new Error('FATAL: Invalid redirect search params'), {extra: {params}});
      return;
    }

    const redirect_uri = `${window.location.origin}${window.location.pathname}`;

    postOAuthToken({
      grant_type: 'authorization_code',
      code: params.code,
      code_verifier: flow?.verifier,
      redirect_uri,
      client_id: OAUTH_CLIENT_ID,
      workspace: params.workspace,
    })
      .unwrap()
      .then((response) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, response.access_token);
        if (response.refresh_token) {
          localStorage.setItem(browserStorageKey.REFRESH_TOKEN, response.refresh_token);
        }

        getDocument360RedirectURl({redirectTo: decodeURIComponent('/')})
          .unwrap()
          .then((res) => res.redirectUrl)
          .then((url) => window.open(url, '_self'));
      })
      .catch(handleApiError);
  });

  return (
    <Center height="100vh" width="100%">
      <VStack spacing={4}>
        <DataStatus
          isLoading
          isError={isError || !isRedirectValid}
          errorMessage={
            isRedirectValid
              ? i18n.t('general.errors.invalidLoginAttempt')
              : i18n.t('general.errors.invalidParams')
          }
        />

        <Show when={isError || !isRedirectValid}>
          <Button
            title={i18n.t('page.login.actions.backToLogin')}
            onClick={() => redirectToLogin()}
          />
        </Show>
      </VStack>
    </Center>
  );
};
